import type { Distribution } from "@memorang/types";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { InsightCallout } from "../DataCards/InsightCallout";
import EmptyState from "../EmptyView";
import { ExamInfoCard, type ExamInfoStat } from "../HomeApplet/ExamInfoCard";
import { TasksSection } from "../HomeApplet/TasksSection";
import type { TasksSectionItem } from "../HomeApplet/types";
import { LessonExploreCard } from "../LessonsExploreScreen/LessonExploreCard";
import type { ExploreLesson } from "../LessonsExploreScreen/types";
import ScrollContainer from "../ScrollContainer";

export type LessonsHomeHero = {
	userName?: string;
	subtitle?: string;
	distribution: Distribution;
	stats: ExamInfoStat[];
	insight?: string;
	isLoading?: boolean;
};

type Props = {
	hero: LessonsHomeHero;
	suggestedLessons: TasksSectionItem[];
	allLessons: ExploreLesson[];
	suggestedTitle?: string;
	allLessonsTitle?: string;
	onLessonPress: (lesson: ExploreLesson) => void;
};

const SectionTitle = ({ children }: { children: string }) => {
	const { isMobile } = useDevice();
	return (
		<Box paddingHorizontal={isMobile ? 16 : 0}>
			<Text variant="titleMedium">{children}</Text>
		</Box>
	);
};

const HeroSection = ({ hero }: { hero: LessonsHomeHero }) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const { t } = useTranslation();

	const title = hero.userName
		? t("lessons.home.hero.greeting", { name: hero.userName })
		: t("lessons.home.hero.greetingNoName");
	const hasInsight = Boolean(hero.insight);

	return (
		<Box gap={2}>
			<ExamInfoCard
				examName={title}
				subtitle={hero.subtitle}
				examEnabled={false}
				accuracy={0}
				distribution={hero.distribution}
				stats={hero.stats}
				isLoading={hero.isLoading}
				style={
					!isMobile && hasInsight
						? {
								borderBottomLeftRadius: theme.roundness,
								borderBottomRightRadius: theme.roundness,
							}
						: undefined
				}
			/>
			{hasInsight ? (
				<Box
					backgroundColor={theme.colors.surface}
					padding={16}
					style={
						isMobile
							? undefined
							: {
									borderTopLeftRadius: theme.roundness,
									borderTopRightRadius: theme.roundness,
									borderBottomLeftRadius: theme.roundness * 3,
									borderBottomRightRadius: theme.roundness * 3,
								}
					}
				>
					<InsightCallout text={hero.insight} variant="flat" />
				</Box>
			) : null}
		</Box>
	);
};

export const LessonsHomeScreen = ({
	hero,
	suggestedLessons,
	allLessons,
	suggestedTitle,
	allLessonsTitle,
	onLessonPress,
}: Props) => {
	const { t } = useTranslation();
	const { isMobile } = useDevice();
	const hasLessons = suggestedLessons.length > 0 || allLessons.length > 0;

	return (
		<Box flex={1} height="100%">
			<ScrollContainer
				contentContainerStyle={{
					paddingBottom: 96,
					paddingTop: isMobile ? 0 : 24,
				}}
				maxWidth="sm"
			>
				<Box gap={24} paddingBottom={24}>
					<HeroSection hero={hero} />
					{suggestedLessons.length > 0 ? (
						<Box gap={16}>
							<SectionTitle>
								{suggestedTitle ?? t("lessons.home.suggested.title")}
							</SectionTitle>
							<TasksSection
								items={suggestedLessons}
								cardVariant={isMobile ? "default" : "detailed"}
								ctaMode={isMobile ? "outlined" : undefined}
							/>
						</Box>
					) : null}
					{allLessons.length > 0 ? (
						<Box gap={16}>
							<SectionTitle>
								{allLessonsTitle ?? t("lessons.home.all.title")}
							</SectionTitle>
							<Box gap={8}>
								{allLessons.map((lesson) => (
									<LessonExploreCard
										key={lesson.id}
										lesson={lesson}
										layout={isMobile ? "stacked" : "row"}
										highlightInProgress
										onPress={onLessonPress}
									/>
								))}
							</Box>
						</Box>
					) : null}
					{hasLessons ? null : (
						<Box flex={1} minHeight={320} paddingTop={24}>
							<EmptyState
								title={t("lessons.home.emptyState.title")}
								subtitle={t("lessons.home.emptyState.subtitle")}
							/>
						</Box>
					)}
				</Box>
			</ScrollContainer>
		</Box>
	);
};
